import { useEffect, useState } from "react";
import { Button, Field, Segmented, Select } from "@agentsaas/ui";
import { useLocation, useNavigate } from "react-router";
import { NotificationPrefsSection } from "../notifications/NotificationCenter";
import { useT } from "../app/i18n";
import { useThemePref, type ThemePref } from "../app/theme";
import { useSession } from "../app/session";
import { LanguageSelect } from "../layout/shell";
import { useServices } from "../app/services";

const TIMEZONES = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf("timeZone");
  } catch {
    return ["UTC"];
  }
})();

export function SettingsPage() {
  const { t } = useT();
  const [theme, setTheme] = useThemePref();
  const session = useSession();
  const navigate = useNavigate();
  const user = session.state.user;
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#notifications") document.getElementById("notifications")?.scrollIntoView?.({ block: "start" });
  }, [hash]);
  const onTheme = (v: ThemePref) => {
    setTheme(v);
    if (user) void session.updateMe({ theme: v }).catch(() => {});
  };
  return (
    <div className="page">
      <div className="page__head">
        <h1 className="page__title">{t("settings.title")}</h1>
      </div>
      <div className="card">
        <Field label={t("settings.language")}>{(id) => <LanguageSelect id={id} />}</Field>
        <div className="ui-field">
          <span className="ui-field__label">{t("settings.theme")}</span>
          <Segmented<ThemePref>
            label={t("settings.theme")}
            value={theme}
            onChange={onTheme}
            options={[
              { value: "system", label: t("theme.system") },
              { value: "light", label: t("theme.light"), icon: "sun" },
              { value: "dark", label: t("theme.dark"), icon: "moon" },
            ]}
          />
        </div>
        {user ? (
          <Field label={t("settings.timezone")}>
            {(id) => (
              <Select id={id} value={user.timezone} onChange={(e) => void session.updateMe({ timezone: e.target.value })}>
                {TIMEZONES.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}
        {user ? <BiometricToggle /> : null}
        {user ? (
          <Button
            variant="secondary"
            onClick={() => void session.logout().then(() => navigate("/login"))}
            data-testid="logout"
          >
            {t("settings.logout")}
          </Button>
        ) : null}
      </div>
      {user ? <NotificationPrefsSection /> : null}
    </div>
  );
}

/** 原生壳：生物识别解锁开关（设备支持时才显示；setEnabled 返回最终状态，用户取消验证时保持原状） */
export function BiometricToggle() {
  const { t } = useT();
  const { native } = useServices();
  const [available, setAvailable] = useState(false);
  const [on, setOn] = useState(() => native?.biometric.enabled() ?? false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!native) return;
    let alive = true;
    void native.biometric
      .available()
      .catch(() => false)
      .then((ok) => alive && setAvailable(ok));
    return () => {
      alive = false;
    };
  }, [native]);
  if (!native || !available) return null;
  const toggle = async () => {
    setBusy(true);
    try {
      setOn(await native.biometric.setEnabled(!on));
    } catch {
      setOn(native.biometric.enabled());
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ui-field switch-row" data-testid="biometric">
      <div className="grow">
        <span id="biometric-title" className="ui-field__label">
          {t("settings.biometric.title")}
        </span>
        <p id="biometric-desc" className="small muted" style={{ margin: 0 }}>
          {t("settings.biometric.desc")}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-labelledby="biometric-title"
        aria-describedby="biometric-desc"
        className={`switch${on ? " is-on" : ""}`}
        disabled={busy}
        onClick={() => void toggle()}
        data-testid="biometric-switch"
      >
        <span className="switch__knob" aria-hidden="true" />
      </button>
    </div>
  );
}
